import React, { ChangeEvent, FC, useCallback, useState } from 'react'
import { ISelectOption } from 'components/forms/Selects'
import { Button } from 'transfix-ui/components/Button'
import { InputMultiSelect } from 'transfix-ui/components/InputMultiSelect'
import { Switch } from 'transfix-ui/components/Switch'
import { makeStyles, Typography } from 'transfix-ui/core'
import { AutocompleteShipper } from 'apps/marketplace/pages/issues/components/AutocompleteShipper/AutocompleteShipper'
import { FilterIcon } from 'transfix-icons'
import { AutocompleteFacility } from './CarrierTrailerFiltersForm/components/AutocompleteFacility/AutocompleteFacility'
import {
  SORT_VALUES_MISSING_TRAILERS,
  TRAILER_STATUS_SELECT_OPTIONS,
  SORT_VALUES_KNOWN_TRAILERS,
} from './constants'
import { TTrailerStates } from './types'

interface IUpdateProp {
  shipperIds?: string[] | null
  facilities?: string[] | null
  trailerStates?: TTrailerStates[]
  showMissingTrailers?: boolean
  sortBy?: string
}

export interface ICarrierTrailerFiltersProps {
  shipperIds: string[]
  facilities: string[]
  trailerStates: TTrailerStates[]
  showMissingTrailers: boolean
  sortBy: string
  onUpdate: (prop: IUpdateProp) => void
  onMoreFiltersClick: () => void
}

const useCarrierTrailerFilterStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1.5),
  },
  filterItem: {
    flex: '1 1 200px',
  },
  filtersButton: {
    maxWidth: '150px',
    '& > span': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      '& svg': {
        marginTop: theme.spacing(0.5),
      },
    },
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
  },
  status: {
    flex: '1 1 200px',
    overflow: 'hidden',
  },
}))

/**
 * These are the filters in the header of the trailer management tab.
 */
const CarrierTrailerFilters: FC<ICarrierTrailerFiltersProps> = ({
  facilities,
  onMoreFiltersClick,
  onUpdate,
  shipperIds,
  showMissingTrailers,
  sortBy,
  trailerStates: trailerStatesProp,
}) => {
  const styles = useCarrierTrailerFilterStyles()
  const [trailerStates, setTrailerStates] = useState<TTrailerStates[]>(trailerStatesProp || [])

  const changeTrailerLocationHandler = (options: ISelectOption[]) => {
    setTrailerStates(options.map(({ value }) => value as TTrailerStates))
  }

  const closeTrailerLocationHandler = () => {
    if (trailerStates.join(',') !== trailerStatesProp.join(','))
      onUpdate({
        trailerStates,
      })
  }

  /**
   * Handles toggling showMissingTrailers on/off
   * The queries used by known and missing trailers take in slightly different sortBy values so
   * this will also handle updating sortBy appropriately based on showMissingTrailers value
   */
  const showMissingTrailersChangeHandler = useCallback(
    ({ target: { checked } }: ChangeEvent<HTMLInputElement>): void => {
      const showMissingTrailersValue = !checked

      const sortValue = showMissingTrailersValue
        ? SORT_VALUES_MISSING_TRAILERS[sortBy]
        : SORT_VALUES_KNOWN_TRAILERS[sortBy]

      onUpdate({
        showMissingTrailers: !checked,
        sortBy: sortValue || sortBy,
      })
    },
    [onUpdate, sortBy]
  )

  return (
    <div className={styles.root}>
      <AutocompleteShipper
        className={styles.filterItem}
        value={shipperIds}
        onChange={shipperIds =>
          onUpdate({
            shipperIds: shipperIds || [],
            facilities: [],
          })
        }
        size='small'
        placeholder='Shippers'
      />
      <AutocompleteFacility
        className={styles.filterItem}
        value={facilities}
        shipperApplicationIds={shipperIds}
        onChange={(facilities: string[] | null) =>
          onUpdate({
            facilities: facilities || [],
          })
        }
        size='small'
        placeholder='Facilities'
        limitTags={1}
      />
      {!showMissingTrailers && (
        <div className={styles.status}>
          <InputMultiSelect
            options={TRAILER_STATUS_SELECT_OPTIONS}
            values={trailerStates}
            onChange={changeTrailerLocationHandler}
            onClose={closeTrailerLocationHandler}
            placeholder='Select Trailer States'
          />
        </div>
      )}
      <div className={styles.switchContainer}>
        <Typography variant='body2'>Missing Trailer #s</Typography>
        <Switch
          label='Known Trailer #s'
          onChange={showMissingTrailersChangeHandler}
          checked={!showMissingTrailers}
        />
      </div>
      <Button color='tertiary' onClick={onMoreFiltersClick} className={styles.filtersButton}>
        <FilterIcon size='large' />
        <Typography variant='button'>More Filters</Typography>
      </Button>
    </div>
  )
}

export default CarrierTrailerFilters
